var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import styled from 'styled-components';
import { colors, fontSizes, fontWeights } from '../../../defaultTheme';
import { getAriaProps } from '../../../helpers/getAriaProps';
import Small from '../../typography/Small';
import { DESCRIPTION_ID_PREFIX } from './constants';
import { handleOnBlur, handleOnChange, handleOnFocus } from './helpers';
const NOT_ALLOWED = 'not-allowed';
const TEXT = 'text';
export const Container = styled.div `
  width: ${({ width }) => width};
  color: ${({ hasError }) => hasError ? colors.error : colors.mediumGrey};
  text-align: left;
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
`;
export const Input = styled.input `
  position: absolute;
  font-size: ${fontSizes.root}px;
  bottom: 5px;
  left: ${({ hasIcon }) => (hasIcon ? '0' : '16px')};
  width: ${({ showMaskedOption, hasIcon }) => {
    if (showMaskedOption && hasIcon)
        return '64%;';
    if (showMaskedOption)
        return '73%;';
    if (hasIcon)
        return 'calc(100% - 16px)';
    return 'calc(100% - 32px);';
}};
  opacity: 1;
  border: none;
  background: none;
  cursor: ${({ disabled }) => (disabled ? NOT_ALLOWED : TEXT)};
  color: ${({ hasContent, disabled }) => hasContent && !disabled ? colors.black : 'inherit'};
  padding: 0;
  &:focus {
    outline: none;
    color: ${colors.black};
  }

  &:hover {
    border: none;
    outline: none;
  }
  &:disabled {
    -webkit-text-fill-color: ${colors.black};
  }
`;
export const Label = styled.label `
  position: absolute;
  font-size: inherit;
  font-family: Open Sans;
  left: ${({ hasIcon }) => (hasIcon ? '0' : '16px')};
  top: calc(50% - 0.7em);
  transition-property: left, top, font-size;
  transition-duration: 0.2s;
  color: ${({ hasContent, hasError, disabled, theme }) => {
    if (hasError)
        return colors.error;
    if (hasContent && !disabled)
        return theme.field.label;
    return colors.mediumGrey;
}};
  cursor: ${({ disabled }) => (disabled ? NOT_ALLOWED : TEXT)};
  text-indent: 0;
  ${({ hasContent }) => hasContent &&
    `
        font-size: ${fontSizes.small}px;
        top: 3px;
      `}

  ${Input}:focus ~ && {
    font-size: ${fontSizes.small}px;
    top: 3px;
    color: ${({ theme, hasError }) => hasError ? colors.error : theme.field.label};
  }

  ${Input}:-webkit-autofill ~ & {
    font-size: ${fontSizes.small}px;
    top: 3px;
    color: ${({ theme }) => theme.field.label};
  }
`;
export const HelpTextWrapper = styled.div `
  &:empty {
    display: none;
  }
  display: flex;
  margin-top: 4px;
  color: inherit;
  align-items: flex-start;
`;
export const HelpText = styled(Small) `
  display: block;
  padding: 0;
  margin: 2.5px 0;
  flex-basis: 100%;
  color: ${({ error }) => error ? colors.error : colors.black};
`;
export const InputWrapper = styled.div `
  color: ${({ hasError }) => hasError ? colors.error : colors.mediumGrey};
  width: 100%;
  height: 48px;
  position: relative;
  border-radius: 6px;
  box-shadow: 0 0 0
    ${({ disabled, hasError }) => {
    if (disabled)
        return `1px ${colors.grey}`;
    if (hasError)
        return `2px ${colors.error}`;
    return `1px ${colors.heavyGrey}`;
}};
  display: flex;
  align-items: center;
  &:hover {
    ${({ hasError, disabled }) => hasError || disabled ? '' : `box-shadow: 0 0 0 2px ${colors.heavyGrey};`}
  }
  cursor: ${({ disabled }) => disabled ? NOT_ALLOWED : TEXT};
  background-color: ${({ disabled }) => disabled ? colors.lightestGrey : colors.white};
  &:focus-within {
    box-shadow: 0 0 0 2px
      ${({ hasError, theme }) => (hasError ? colors.error : theme.field.focus)};
  }
`;
export const RightIconWrapper = styled.div `
  padding: 3px;
  width: 32px;
  margin-right: 8px;
  height: 28px;
  width: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 16px;
  cursor: pointer;
  color: ${({ hasError, theme }) => hasError ? colors.error : theme.field.icon};
`;
export const InputTextContainer = styled.div `
  display: flex;
  position: relative;
  height: 100%;
  width: 100%;
  &:focus-within + ${RightIconWrapper} {
    background: ${({ hasError, theme }) => !hasError && theme.hoverBackground};
    color: ${({ theme, hasError }) => !hasError && theme.field.focus};
  }
`;
export const ShowHideWrapper = styled.div `
  position: absolute;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ isShowHideTypeString }) => isShowHideTypeString ? '22%' : 'auto'};
  font-weight: ${fontWeights.regular};
  color: ${({ hasError, theme }) => (hasError ? colors.error : theme.link)};

  > span {
    user-select: none;
    cursor: pointer;
    border: none;
    background: none;
    &:focus {
      outline: ${({ theme }) => theme.field.focus || '-webkit-focus-ring-color'}
        auto 5px;
    }
  }
`;
export const LeftIconWrapper = styled.div `
  width: 44px;
  min-width: 44px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ hasError, theme }) => hasError ? colors.error : theme.field.icon};
`;
export const ErrorIconWrapper = styled.div `
  height: 100%;
  margin: 4px 4px 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;
export const ErrorOrHelpWrapper = styled.div `
  display: flex;
`;
export const ErrorOrHelp = ({ error, errorIcon, id, helpText, className }) => {
    return (_jsxs(ErrorOrHelpWrapper, Object.assign({ className: className }, { children: [errorIcon && error && _jsx(ErrorIconWrapper, { children: errorIcon }), _jsx(HelpText, Object.assign({ id: `${DESCRIPTION_ID_PREFIX}-${id}`, error: error }, { children: error || helpText }))] })));
};
export const InputText = forwardRef((_a, ref) => {
    var _b;
    var { error, id, inputMode, maxLength, inputClassName, helpText, required, autoCapitalize, name, typeValue, leftIcon, isDisabled, isReadOnly, hasContent, inputValue, matchPattern, isInputTypeTel, onChange, onBlur, onFocus, setTelPlaceholder, onKeyPress, onKeyDown, onKeyUp, showMaskedOption, autoComplete, inputPlaceholder, dataCy, placeholder } = _a, props = __rest(_a, ["error", "id", "inputMode", "maxLength", "inputClassName", "helpText", "required", "autoCapitalize", "name", "typeValue", "leftIcon", "isDisabled", "isReadOnly", "hasContent", "inputValue", "matchPattern", "isInputTypeTel", "onChange", "onBlur", "onFocus", "setTelPlaceholder", "onKeyPress", "onKeyDown", "onKeyUp", "showMaskedOption", "autoComplete", "inputPlaceholder", "dataCy", "placeholder"]);
    const descriptionId = `${DESCRIPTION_ID_PREFIX}-${id}`;
    const ariaProps = (_b = getAriaProps(props)) !== null && _b !== void 0 ? _b : {};
    return (_jsxs(InputTextContainer, Object.assign({ hasError: !!error }, { children: [_jsx(Input, Object.assign({}, props, { id: id, inputMode: inputMode, maxLength: maxLength, className: inputClassName, "aria-describedby": descriptionId, "aria-invalid": !!error, "aria-required": required, autoCapitalize: autoCapitalize, name: name, type: typeValue, ref: ref, hasIcon: !!leftIcon, disabled: isDisabled, readOnly: isReadOnly, hasContent: hasContent, value: inputValue, onChange: (event) => handleOnChange({
                    event,
                    matchPattern,
                    onChange
                }), showMaskedOption: showMaskedOption, onBlur: (event) => handleOnBlur({
                    event,
                    onBlur,
                    setTelPlaceholder,
                    isInputTypeTel
                }), onKeyPress: onKeyPress, onKeyUp: onKeyUp, onKeyDown: onKeyDown, required: required, autoComplete: autoComplete, placeholder: inputPlaceholder, onFocus: (event) => {
                    handleOnFocus({
                        event,
                        setTelPlaceholder,
                        isInputTypeTel,
                        onFocus
                    });
                }, "data-cy": dataCy && `${dataCy}.input` }, ariaProps)), _jsx(Label, Object.assign({ htmlFor: id, hasError: !!error, disabled: isDisabled, hasIcon: !!leftIcon, hasContent: hasContent, "data-cy": dataCy && `${dataCy}.label` }, { children: placeholder }))] })));
});
//# sourceMappingURL=subcomponents.js.map